import {
  AfterViewInit,
  Directive,
  ElementRef,
  Input,
  OnDestroy,
} from '@angular/core';

@Directive({
  selector: '[appStickyTableHeader]',
})
export class StickyTableHeaderDirective implements AfterViewInit, OnDestroy {
  //#region Inputs

  @Input({ required: true }) tableContainer!: HTMLElement;
  @Input({ required: true }) rootMargin!: string;
  @Input() isEnabled = true;

  //#endregion
  private readonly intersectionThreshold: number = 0.99;

  private intersectionObserver!: IntersectionObserver;
  private resizeObserver!: ResizeObserver;
  private scrollHandler = this.syncScroll.bind(this);
  private groupCellsColspans: number[][];
  private intersectionRatio: number;

  constructor(private hostElement: ElementRef) {}

  //#region Getters

  private get placeholder(): HTMLElement {
    return this.tableContainer.children.item(0) as HTMLElement;
  }

  private get table(): HTMLElement {
    return this.tableContainer.children.item(1) as HTMLElement;
  }

  private get thead(): HTMLElement {
    return this.table.children.item(0) as HTMLElement;
  }

  private get trs(): HTMLElement[] {
    return Array.prototype.slice.call(this.thead.children);
  }

  //#endregion

  //#region Lifecycle hooks

  ngAfterViewInit(): void {
    if (!this.isEnabled) {
      return;
    }

    // Timeout is needed to execute this after grid initialization
    setTimeout(() => {
      this.observe();
      this.observeResize();
    });
  }

  ngOnDestroy(): void {
    if (!this.isEnabled) {
      return;
    }

    this.removeScrollEventListener();
    this.intersectionObserver?.disconnect();
    this.resizeObserver?.disconnect();
  }

  //#endregion

  //#region Private methods

  private observeResize(): void {
    this.resizeObserver = new ResizeObserver(() => {
      if (this.intersectionRatio < this.intersectionThreshold) {
        for (const tr of this.trs) {
          tr.style.display = 'table';
          tr.style.width = `${this.tableContainer.scrollWidth}px`;
        }

        this.thead.style.maxWidth = `${this.tableContainer.clientWidth}px`;
      }
    });

    this.resizeObserver.observe(this.tableContainer);
  }

  private observe(): void {
    this.intersectionObserver = new IntersectionObserver(
      ([e]) => {
        this.intersectionRatio = e.intersectionRatio;

        this.thead.classList.toggle(
          'table-header-sticky',
          e.intersectionRatio < this.intersectionThreshold
        );

        // Intersected
        if (e.intersectionRatio < this.intersectionThreshold) {
          this.syncScroll();
          this.addScrollEventListener();

          this.placeholder.style.display = 'block';
          for (const tr of this.trs) {
            tr.style.display = 'table';
            tr.style.width = `${this.tableContainer.scrollWidth}px`;
          }

          this.computeGroupWidthsOnStickyActive(this.trs);

          this.thead.style.maxWidth = `${this.tableContainer.clientWidth}px`;
        }

        // Not intersected
        if (e.intersectionRatio >= this.intersectionThreshold) {
          this.resetScroll();
          this.removeScrollEventListener();

          this.placeholder.style.display = 'none';
          for (const tr of this.trs) {
            tr.style.display = 'table-row';
          }

          this.reapplyGroupColspans(this.trs);
        }
      },
      {
        rootMargin: this.rootMargin,
        threshold: [this.intersectionThreshold],
      }
    );

    this.intersectionObserver.observe(this.hostElement.nativeElement);
  }

  private syncScroll(): void {
    for (const tr of this.trs) {
      tr.style.transform = `translateX(-${this.tableContainer.scrollLeft}px)`;
    }
  }

  private resetScroll(): void {
    for (const tr of this.trs) {
      tr.style.transform = 'translateX(0px)';
    }
  }

  private addScrollEventListener(): void {
    this.tableContainer.addEventListener('scroll', this.scrollHandler);
  }

  private removeScrollEventListener(): void {
    this.tableContainer.removeEventListener('scroll', this.scrollHandler);
  }

  private computeGroupWidthsOnStickyActive(trList: HTMLElement[]): void {
    const displayedColumnHeadersRow = trList.find(
      (x) => x.id === 'displayedColumnsHeaderRow'
    );

    if (!displayedColumnHeadersRow) {
      return;
    }

    const otherTrs = trList.filter((x) => x.id !== 'displayedColumnsHeaderRow');
    const columnHeaderRowCells = Array.prototype.slice.call(
      trList.find((x) => x.id === 'displayedColumnsHeaderRow').children
    ) as HTMLElement[];

    this.groupCellsColspans = [];

    for (const tr of otherTrs) {
      const groupColspan = [];
      let index = 0;

      for (const cell of Array.prototype.slice.call(
        tr.children
      ) as HTMLElement[]) {
        // get the collspan and add it in memory to be available
        // when the table will no longer be sticky
        const cellColspan = +(cell.getAttribute('colspan') ?? 1);
        groupColspan.push(cellColspan);

        // compute the width based on the columnWidth on the basic
        // column row and cell collspan
        const computedWidth = columnHeaderRowCells
          .slice(index, index + cellColspan)
          .map((x) => x.getBoundingClientRect().width)
          .reduce((acc, val) => (acc += val), 0.0);

        // set the min and current width to the computed value
        // set the colspan to 0
        cell.style.width = `${computedWidth}px`;
        cell.style.minWidth = `${computedWidth}px`;
        cell.setAttribute('colspan', '0');

        // increase the cell index to make sure we fetch the
        // coresponding cells width for the next cell
        index += cellColspan;
      }

      // push the list of collspans for the current group inside the memory array
      this.groupCellsColspans.push(groupColspan);
    }
  }

  private reapplyGroupColspans(trList: HTMLElement[]): void {
    // if the groupsColspans is undefined (the user didn't scoll down yet), abort
    if (!this.groupCellsColspans) {
      return;
    }

    const otherHeaderTrs = trList.filter(
      (x) => x.id !== 'displayedColumnsHeaderRow'
    );

    for (let i = 0; i < otherHeaderTrs.length; i++) {
      let j = 0;
      for (const cell of Array.prototype.slice.call(
        otherHeaderTrs[i].children
      ) as HTMLElement[]) {
        // for each cell, set the width and minimum width to 0
        // apply the corresponding cellColSpan
        cell.style.width = '';
        cell.style.minWidth = '';
        cell.setAttribute('colSpan', this.groupCellsColspans[i][j].toString());
        j++;
      }
    }
  }

  //#endregion
}
